import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Paper,
  Breadcrumbs,
  Link,
  Button
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import TimeSeriesChart from '../TimeSeriesChart/TimeSeriesChart';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import { forecastHierarchyService } from '../../services/forecastHierarchy.service';

const NodeDetailsPage = () => {
  const { runId, hierarchyId, nodeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract forecast start date from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const forecastStartDateParam = queryParams.get('forecastStartDate');
  const forecastStartDate = forecastStartDateParam ? new Date(forecastStartDateParam) : null;
  
  const [nodeData, setNodeData] = useState(null);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [childrenNodes, setChildrenNodes] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug logging
  console.log('NodeDetailsPage received params:', { runId, hierarchyId, nodeId });
  console.log('NodeDetailsPage: Component mounted');
  console.log('NodeDetailsPage: Forecast start date from URL:', forecastStartDate);
  console.log('NodeDetailsPage: Current nodeData:', nodeData);
  console.log('NodeDetailsPage: Current hierarchyData:', hierarchyData);

  // Test node details API directly
  useEffect(() => {
    const testNodeAPI = async () => {
      console.log('Testing node details API for nodeId:', nodeId);
      try {
        const response = await forecastHierarchyService.getNodeDetails(hierarchyId, nodeId);
        console.log('Direct node API test response:', response);
      } catch (error) {
        console.error('Direct node API test failed:', error);
      }
    };
    
    if (nodeId) {
      testNodeAPI();
    }
  }, [nodeId, hierarchyId]);

  useEffect(() => {
    const fetchNodeData = async () => {
      console.log('NodeDetailsPage: fetchNodeData called with:', { runId, hierarchyId, nodeId });
      
      if (!runId || !hierarchyId || !nodeId) {
        console.error('NodeDetailsPage: Missing required parameters:', { runId, hierarchyId, nodeId });
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch all data in parallel for better performance
        const [hierarchyResponse, nodeResponse, childrenResponse, valuesResponse] = await Promise.allSettled([
          forecastHierarchyService.getHierarchyById(hierarchyId),
          forecastHierarchyService.getNodeDetails(hierarchyId, nodeId),
          forecastHierarchyService.getChildNodes(hierarchyId, nodeId),
          forecastHierarchyService.getNodeValues(hierarchyId, nodeId, { run_id: runId })
        ]);

        // Process hierarchy data
        if (hierarchyResponse.status === 'fulfilled' && hierarchyResponse.value.status === 'success') {
          console.log('NodeDetailsPage: Hierarchy API succeeded:', hierarchyResponse.value);
          setHierarchyData(hierarchyResponse.value.data.hierarchy);
        } else {
          console.log('NodeDetailsPage: Hierarchy API failed:', hierarchyResponse);
        }

        // Process node data
        if (nodeResponse.status === 'fulfilled' && nodeResponse.value.status === 'success') {
          // Node data is directly at top level of response (not under data.node)
          const nodeData = nodeResponse.value.data?.node || nodeResponse.value.node_details || {
            node_id: nodeResponse.value.node_id,
            node_name: nodeResponse.value.node_name,
            node_key: nodeResponse.value.node_key,
            node_path: nodeResponse.value.node_path,
            parent_node_id: nodeResponse.value.parent_node_id,
            sku_count: nodeResponse.value.sku_count,
            store_count: nodeResponse.value.store_count,
            hierarchy_id: nodeResponse.value.hierarchy_id,
            level_id: nodeResponse.value.level_id,
            level_name: nodeResponse.value.level_name,
            display_name: nodeResponse.value.display_name,
            level_order: nodeResponse.value.level_order,
            is_leaf_level: nodeResponse.value.is_leaf_level,
            hierarchy_name: nodeResponse.value.hierarchy_name,
            hierarchy_type: nodeResponse.value.hierarchy_type
          };
          console.log('NodeDetailsPage: Node API response:', nodeResponse.value);
          console.log('NodeDetailsPage: Setting nodeData to:', nodeData);
          setNodeData(nodeData);
        } else {
          console.log('NodeDetailsPage: Node API failed:', nodeResponse);
          // Try to get node details from summary endpoint as fallback
          try {
            console.log('NodeDetailsPage: Trying summary endpoint as fallback for node details...');
            const summaryResponse = await forecastHierarchyService.getNodeValuesSummary(hierarchyId, nodeId, { run_id: runId });
            if (summaryResponse.status === 'success' && summaryResponse.data?.node_details) {
              console.log('NodeDetailsPage: Got node details from summary endpoint:', summaryResponse.data.node_details);
              setNodeData(summaryResponse.data.node_details);
            } else {
              console.log('NodeDetailsPage: Summary endpoint also failed for node details');
            }
          } catch (summaryErr) {
            console.error('NodeDetailsPage: Summary endpoint failed for node details:', summaryErr);
          }
        }

        // Process children data
        if (childrenResponse.status === 'fulfilled' && childrenResponse.value.status === 'success') {
          const children = childrenResponse.value.data?.children || childrenResponse.value.children || [];
          console.log('NodeDetailsPage: Children API succeeded:', childrenResponse.value);
          setChildrenNodes(children);
        } else {
          console.log('NodeDetailsPage: Children API failed:', childrenResponse);
        }

        // Process time series data
        if (valuesResponse.status === 'fulfilled' && valuesResponse.value.status === 'success') {
          const timeSeriesData = valuesResponse.value.time_series_data || valuesResponse.value.data?.values || valuesResponse.value.values || [];
          console.log('NodeDetailsPage: Main values API succeeded, data:', timeSeriesData);
          setTimeSeriesData(timeSeriesData);
        } else if (valuesResponse.status === 'rejected' || (valuesResponse.status === 'fulfilled' && valuesResponse.value.status !== 'success')) {
          // Try fallback to summary endpoint
          console.log('NodeDetailsPage: Main values API failed, trying summary fallback...');
          try {
            const summaryResponse = await forecastHierarchyService.getNodeValuesSummary(hierarchyId, nodeId, { run_id: runId });
            console.log('NodeDetailsPage: Full summary response:', JSON.stringify(summaryResponse, null, 2));
            
            if (summaryResponse.status === 'success' && summaryResponse.data) {
              // Extract node details from summary response if not already set
              const nodeDetails = summaryResponse.data.node_details;
              if (nodeDetails && !nodeData) {
                console.log('NodeDetailsPage: Setting node data from summary:', nodeDetails);
                setNodeData(nodeDetails);
              }
              
              // Convert summary stats to time series format
              const summary = summaryResponse.data.summary;
              if (summary && summary.organic_stats) {
                const convertedData = [{
                  forecast_period: summary.date_range.start,
                  organic_p50: summary.organic_stats.p50.avg,
                  organic_p10: summary.organic_stats.p10.avg,
                  organic_p90: summary.organic_stats.p90.avg,
                  external_p50: summary.external_stats.p50.avg,
                  external_p10: summary.external_stats.p10.avg,
                  external_p90: summary.external_stats.p90.avg
                }];
                console.log('NodeDetailsPage: Converted summary data to time series:', convertedData);
                setTimeSeriesData(convertedData);
              } else {
                console.log('NodeDetailsPage: No summary stats found in response');
                setTimeSeriesData([]);
              }
            } else {
              console.log('NodeDetailsPage: Summary API failed or no data');
              setTimeSeriesData([]);
            }
          } catch (summaryErr) {
            console.error('NodeDetailsPage: Summary API also failed:', summaryErr);
            setTimeSeriesData([]);
          }
        } else {
          console.log('NodeDetailsPage: Values API not fulfilled or failed');
          setTimeSeriesData([]);
        }
        
      } catch (err) {
        console.error('Error fetching node data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNodeData();
  }, [runId, hierarchyId, nodeId]);

  const handleBackToHierarchy = () => {
    navigate(`/forecast-hierarchy/${runId}`);
  };

  const handleChildClick = (childNode) => {
    // Pass forecast start date to child node page
    const queryParams = forecastStartDate ? `?forecastStartDate=${forecastStartDate.toISOString().split('T')[0]}` : '';
    navigate(`/forecast-hierarchy/${runId}/hierarchy/${hierarchyId}/node/${childNode.node_id}${queryParams}`);
  };

  const handleHomeClick = () => {
    navigate('/forecast-runs');
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading node details...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToHierarchy}
          sx={{ mr: 2 }}
        >
          Back to Hierarchy
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<HomeIcon />}
          onClick={handleHomeClick}
        >
          Back to Forecast Runs
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation
        runId={runId}
        hierarchyId={hierarchyId}
        nodeId={nodeId}
        nodeData={nodeData}
        hierarchyData={hierarchyData}
        onBackToHierarchy={handleBackToHierarchy}
        onHomeClick={handleHomeClick}
      />

      {/* Debug Section - Remove this after fixing */}
      <Box sx={{ mb: 2, p: 1, backgroundColor: '#f0f0f0', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Debug Info: nodeData={nodeData ? 'loaded' : 'null'}, 
          nodeData.parent_node_id={nodeData?.parent_node_id || 'null'}
        </Typography>
      </Box>

      {/* Node Info */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={nodeData?.node_name || nodeData?.node_key || `Node ${nodeId}`}
          subheader={
            nodeData && (
              <>
                {nodeData.level_name && (
                  <span>Level: {nodeData.level_name} &nbsp;|&nbsp; </span>
                )}
                {nodeData.node_key && (
                  <span>Key: {nodeData.node_key} &nbsp;|&nbsp; </span>
                )}
                {nodeData.sku_count !== undefined && (
                  <span>SKUs: {nodeData.sku_count} &nbsp;|&nbsp; </span>
                )}
                {nodeData.store_count !== undefined && (
                  <span>Stores: {nodeData.store_count}</span>
                )}
              </>
            )
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Node Path: {nodeData?.node_path || 'N/A'}
          </Typography>
        </CardContent>
      </Card>

      {/* Time Series Chart */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Forecast Time Series" />
        <CardContent>
          <TimeSeriesChart
            timeSeriesData={timeSeriesData}
            forecastStartDate={forecastStartDate}
            title={`Forecast for Node: ${nodeData?.node_name || nodeId}`}
            subtitle={hierarchyData?.hierarchy_name || ''}
            groupingConfig={{ groupBy: 'node_name', seriesTypes: ['aggregated', 'external'] }}
            seriesConfig={{
              aggregated: {
                mean: 'organic_p50',
                lower: 'organic_p10',
                upper: 'organic_p90',
                label: 'Aggregated'
              },
              external: {
                mean: 'external_p50',
                lower: 'external_p10',
                upper: 'external_p90',
                label: 'External'
              }
            }}
            initialWindowWeeks={8}
            showControls={true}
            showLegend={true}
            height="500px"
          />
        </CardContent>
      </Card>

      {/* Children Nodes */}
      {childrenNodes.length > 0 && (
        <Card>
          <CardHeader
            title="Child Nodes"
            subheader={`${childrenNodes.length} child nodes available`}
          />
          <CardContent>
            <List>
              {childrenNodes.map((child, index) => (
                <React.Fragment key={child.node_id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleChildClick(child)}>
                      <ListItemText
                        primary={child.node_name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              ID: {child.node_id} • Level: {child.level_name}
                            </Typography>
                            <Typography variant="caption" display="block">
                              SKUs: {child.sku_count} • Stores: {child.store_count}
                            </Typography>
                            {child.node_path && (
                              <Typography variant="caption" display="block">
                                Path: {child.node_path}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Chip 
                        label="View" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < childrenNodes.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {childrenNodes.length === 0 && (
        <Card>
          <CardHeader
            title="Child Nodes"
            subheader="No child nodes available"
          />
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              This node has no child nodes or all child nodes are empty.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default NodeDetailsPage; 